import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sparkles, ArrowRight, Building2, LogOut } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { useCreateBusiness, useUpdateBusiness, useMyBusiness } from "../hooks/useBusiness";
import { useToast } from "../hooks/useToast";
import type { ApiError } from "../api/http";
import { BrandMark } from "../components/BrandMark";
import { authInputBase, authPrimaryBtn } from "../components/auth/authStyles";
import { IndustryPicker } from "../components/onboarding/IndustryPicker";
import { OnboardingModeModal } from "../components/onboarding/OnboardingModeModal";
import { OnboardingPresetStep } from "../components/onboarding/OnboardingPresetStep";
import { clearSetupInProgress, setSetupInProgress } from "../utils/setupFlag";
import {
  CURATED_TIMEZONES,
  CURRENCY_OPTIONS,
  LANGUAGE_OPTIONS,
  allTimezones,
  countryDefaults,
} from "../utils/locale";
import type { BusinessLanguage } from "../api/types";

function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Mexico_City";
  } catch {
    return "America/Mexico_City";
  }
}

/**
 * Progressive single-screen setup: name + industry + country, all on one form.
 * Everything else (logo, theme, invites, address) becomes a dismissible card
 * on the dashboard via DeferredSetupCards.
 */
export function BusinessSetupPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { data: business } = useMyBusiness();
  const createBusiness = useCreateBusiness();
  const updateBusiness = useUpdateBusiness();
  const { addToast } = useToast();

  const [name, setName] = useState("");
  const [industry, setIndustry] = useState("");
  const [country, setCountry] = useState("MX");
  const [currency, setCurrency] = useState(() => countryDefaults("MX").currency);
  const [timezone, setTimezone] = useState(() => browserTimezone());
  const [language, setLanguage] = useState<BusinessLanguage>(
    () => countryDefaults("MX").language,
  );
  const [tzPickerExpanded, setTzPickerExpanded] = useState(false);
  const [tzFilter, setTzFilter] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showModeModal, setShowModeModal] = useState(false);
  const [showPresetStep, setShowPresetStep] = useState(false);
  // Track whether the user has manually overridden the country-driven defaults
  // so we don't keep clobbering their pick when the country changes.
  const [currencyTouched, setCurrencyTouched] = useState(false);
  const [timezoneTouched, setTimezoneTouched] = useState(false);
  const [languageTouched, setLanguageTouched] = useState(false);

  const fullTimezones = useMemo(() => allTimezones(), []);
  const inCurated = CURATED_TIMEZONES.includes(timezone);
  // 400+ IANA zones make the expanded <select> unmanageable, especially on
  // mobile — the filter narrows it by substring. The current pick always
  // stays listed so the select's value never dangles.
  const visibleTimezones = useMemo(() => {
    const base = tzPickerExpanded || !inCurated ? fullTimezones : CURATED_TIMEZONES;
    const query = tzFilter.trim().toLowerCase();
    if (!tzPickerExpanded || !query) return base;
    const filtered = base.filter((tz) => tz.toLowerCase().includes(query));
    return filtered.includes(timezone) ? filtered : [timezone, ...filtered];
  }, [tzPickerExpanded, inCurated, fullTimezones, tzFilter, timezone]);

  // Resume: if business already exists (e.g. refresh), prefill and show a
  // confirmation state rather than creating again.
  useEffect(() => {
    if (business) {
      setName(business.name);
      setIndustry(business.industry ?? "");
      setCountry(business.country ?? "MX");
      if (business.currency) {
        setCurrency(business.currency);
        setCurrencyTouched(true);
      }
      if (business.timezone) {
        setTimezone(business.timezone);
        setTimezoneTouched(true);
      }
      if (business.language) {
        setLanguage(business.language);
        setLanguageTouched(true);
      }
      // If a returning user without a mode picked lands here, show the modal
      // so they can decide before continuing.
      if (business.onboarding_mode === null) {
        setShowModeModal(true);
      }
    }
  }, [business]);

  // When the country changes, smart-default currency + timezone + language —
  // but never clobber a value the user has explicitly chosen.
  useEffect(() => {
    const d = countryDefaults(country);
    if (!currencyTouched) setCurrency(d.currency);
    if (!timezoneTouched) setTimezone(d.timezone);
    if (!languageTouched) setLanguage(d.language);
  }, [country, currencyTouched, timezoneTouched, languageTouched]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = t("onboarding.validation.nameRequired");
    if (!industry) newErrors.industry = t("onboarding.validation.industryRequired");
    setErrors(newErrors);
    if (Object.keys(newErrors).length) return;

    setSubmitting(true);
    // Set the in-progress flag BEFORE the request: the mutation publishes the
    // new business to the query cache before this await resumes, and SetupGate
    // must already see the flag at that moment — otherwise it swaps in the app
    // shell and unmounts this page together with the mode modal it is about to
    // open.
    setSetupInProgress();
    try {
      const payload = { name, industry, country, currency, timezone, language };
      if (business) {
        await updateBusiness.mutateAsync(payload);
      } else {
        await createBusiness.mutateAsync(payload);
      }
      addToast(t("onboarding.created"), "success");
      // Open the pick-mode modal — navigation to / happens on its onPicked.
      setShowModeModal(true);
    } catch (err) {
      // The save didn't go through — drop the flag so the gate doesn't pin
      // the user to this screen for the rest of the session.
      clearSetupInProgress();
      const apiErr = err as ApiError;
      addToast(apiErr.detail ?? "Error", "error");
    } finally {
      setSubmitting(false);
    }
  };

  // The modal sequence is done — release the gate and enter the app.
  const finishSetup = () => {
    clearSetupInProgress();
    navigate("/", { replace: true });
  };

  const handleLogout = async () => {
    clearSetupInProgress();
    await logout();
    navigate("/login");
  };

  const lang = (i18n.language ?? "en").split("-")[0];

  return (
    // `dark` on the root pins this page to the dark look regardless of the
    // user's theme toggle: the shared authStyles constants are theme-aware
    // (light base + dark: variants) and this page's own chrome is dark-only.
    <div className="dark min-h-screen bg-slate-950 text-white flex flex-col">
      <header className="flex items-center justify-between px-4 sm:px-6 py-5 border-b border-slate-800/60">
        <div className="flex items-center gap-2">
          <BrandMark size={30} />
          <span className="font-bold text-lg tracking-tight">Adam</span>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="flex items-center bg-slate-800/60 rounded-full p-0.5 border border-slate-700/50 text-xs">
            <button
              onClick={() => i18n.changeLanguage("en")}
              className={`px-3 py-1 rounded-full font-semibold transition-colors ${lang === "en" ? "bg-blue-600" : "text-slate-400 hover:text-white"}`}
            >
              EN
            </button>
            <button
              onClick={() => i18n.changeLanguage("es")}
              className={`px-3 py-1 rounded-full font-semibold transition-colors ${lang === "es" ? "bg-blue-600" : "text-slate-400 hover:text-white"}`}
            >
              ES
            </button>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-slate-400 hover:text-white hover:bg-slate-800/60 transition-colors"
          >
            <LogOut size={14} />
            <span className="hidden sm:inline">{t("nav.logout")}</span>
          </button>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-10 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-[-20%] left-[-10%] w-[55%] h-[55%] rounded-full glow [--glow:theme(colors.blue.500/15%)]" />
          <div className="absolute bottom-[-20%] right-[-10%] w-[55%] h-[55%] rounded-full glow [--glow:theme(colors.indigo.500/15%)]" />
        </div>

        <form
          onSubmit={handleSubmit}
          className="relative w-full max-w-xl rounded-3xl bg-slate-900/85 md:bg-slate-900/70 md:backdrop-blur border border-slate-800/80 shadow-2xl shadow-black/50 p-7 sm:p-9"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-300 text-xs font-medium mb-5">
            <Sparkles size={13} />
            {t("onboarding.badge")}
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold leading-tight">
            {t("onboarding.title")}
          </h1>
          <p className="text-slate-400 mt-2 text-sm leading-relaxed">
            {t("onboarding.subtitle")}
          </p>

          <div className="mt-6 space-y-5">
            <div>
              <label htmlFor="name" className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1.5">
                {t("onboarding.fields.name")}
              </label>
              <div className="relative">
                <Building2 size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  id="name"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setErrors((p) => ({ ...p, name: "" }));
                  }}
                  required
                  autoFocus
                  className={`${authInputBase} pl-9`}
                  placeholder={t("onboarding.placeholders.name")}
                />
              </div>
              {errors.name && <p className="text-xs text-rose-400 mt-1">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">
                {t("onboarding.fields.industry")}
              </label>
              <IndustryPicker
                value={industry}
                onChange={(key) => {
                  setIndustry(key);
                  setErrors((p) => ({ ...p, industry: "" }));
                }}
              />
              {errors.industry && <p className="text-xs text-rose-400 mt-2">{errors.industry}</p>}
            </div>

            <div>
              <label htmlFor="country" className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1.5">
                {t("onboarding.fields.country")}
              </label>
              <select
                id="country"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className={authInputBase}
              >
                <option value="MX">🇲🇽 México</option>
                <option value="US">🇺🇸 United States</option>
                <option value="CO">🇨🇴 Colombia</option>
                <option value="AR">🇦🇷 Argentina</option>
                <option value="CL">🇨🇱 Chile</option>
                <option value="ES">🇪🇸 España</option>
                <option value="PE">🇵🇪 Perú</option>
                <option value="OT">{t("onboarding.countryOther")}</option>
              </select>
            </div>

            <div>
              <label htmlFor="currency" className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1.5">
                {t("onboarding.fields.currency")}
              </label>
              <select
                id="currency"
                value={currency}
                onChange={(e) => {
                  setCurrency(e.target.value);
                  setCurrencyTouched(true);
                }}
                className={authInputBase}
              >
                {CURRENCY_OPTIONS.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-[11px] text-slate-500">{t("onboarding.currencyHelp")}</p>
            </div>

            <div>
              <label htmlFor="timezone" className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1.5">
                {t("onboarding.fields.timezone")}
              </label>
              {tzPickerExpanded && (
                <input
                  type="search"
                  value={tzFilter}
                  onChange={(e) => setTzFilter(e.target.value)}
                  placeholder={t("onboarding.timezoneSearch")}
                  aria-label={t("onboarding.timezoneSearch")}
                  className={`${authInputBase} mb-1.5`}
                />
              )}
              <select
                id="timezone"
                value={timezone}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "__other__") {
                    setTzPickerExpanded(true);
                    return;
                  }
                  setTimezone(v);
                  setTimezoneTouched(true);
                }}
                className={authInputBase}
              >
                {visibleTimezones.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
                {!tzPickerExpanded && inCurated && (
                  <option value="__other__">{t("onboarding.timezoneOther")}</option>
                )}
              </select>
              <p className="mt-1 text-[11px] text-slate-500">{t("onboarding.timezoneHelp")}</p>
            </div>

            <div>
              <label htmlFor="language" className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1.5">
                {t("onboarding.fields.language")}
              </label>
              <select
                id="language"
                value={language}
                onChange={(e) => {
                  setLanguage(e.target.value as BusinessLanguage);
                  setLanguageTouched(true);
                }}
                className={authInputBase}
              >
                {LANGUAGE_OPTIONS.map((opt) => (
                  <option key={opt.code} value={opt.code}>
                    {t(opt.labelKey as Parameters<typeof t>[0])}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-[11px] text-slate-500">{t("onboarding.languageHelp")}</p>
            </div>
          </div>

          <p className="text-[11px] text-slate-500 mt-5 leading-relaxed">
            {t("onboarding.privacyNote")}
          </p>

          <button
            type="submit"
            disabled={submitting || !name.trim() || !industry}
            className={`${authPrimaryBtn} mt-5 flex items-center justify-center gap-2`}
          >
            {submitting ? t("onboarding.creating") : t("onboarding.continue")}
            {!submitting && <ArrowRight size={16} />}
          </button>

          <div className="mt-5 flex items-center gap-3 text-[11px] text-slate-500">
            <span className="flex-1 h-px bg-slate-800" />
            <span>{t("onboarding.nextStepsHint")}</span>
            <span className="flex-1 h-px bg-slate-800" />
          </div>
          <ul className="mt-3 text-xs text-slate-400 space-y-1">
            <li>✓ {t("onboarding.deferred.starterData")}</li>
            <li>✓ {t("onboarding.deferred.tour")}</li>
            <li>✓ {t("onboarding.deferred.customize")}</li>
          </ul>
        </form>
      </main>

      {showModeModal && (
        <OnboardingModeModal
          onPicked={() => {
            setShowModeModal(false);
            // Only show the preset step if the business hasn't already picked one
            // (existing returning users may have already chosen via the force-picker).
            if (!business?.business_type) {
              setShowPresetStep(true);
            } else {
              finishSetup();
            }
          }}
        />
      )}
      {showPresetStep && (
        <OnboardingPresetStep onDone={finishSetup} />
      )}
    </div>
  );
}
